import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '@/app/api';
import { UiButton, UiDateRangePicker, UiLinkButton } from '@shared/common/ui';

const moneyFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatMoney(value) {
  return moneyFormatter.format(Number(value) || 0);
}

function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function currentMonthPeriod() {
  const now = new Date();
  return {
    from: toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: toIsoDate(now),
  };
}

function formatPeriodLabel({ from, to }) {
  const formatDate = (value) => new Intl.DateTimeFormat('ru-RU').format(new Date(`${value}T00:00:00`));
  if (from && to) return `${formatDate(from)} — ${formatDate(to)}`;
  if (from) return `с ${formatDate(from)}`;
  if (to) return `по ${formatDate(to)}`;
  return 'за всё время';
}

/**
 * Сводка расходов по всем филиалам и общим записям кабинета.
 * Фактический — уже потраченное + зарплаты за прошедшие занятия.
 * Планируемый — зарплаты за занятия, которые ещё не прошли.
 */
export function ProfileExpensesWidget() {
  const [period, setPeriod] = useState(currentMonthPeriod);
  const [appliedPeriod, setAppliedPeriod] = useState(currentMonthPeriod);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('expenses');

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/owner/finance/expenses', {
        params: {
          ...(appliedPeriod.from ? { from: appliedPeriod.from } : {}),
          ...(appliedPeriod.to ? { to: appliedPeriod.to } : {}),
        },
      });
      setOverview(data.overview ?? null);
    } catch (err) {
      setOverview(null);
      setError(err?.response?.data?.message ?? err?.message ?? 'Не удалось загрузить расходы');
    } finally {
      setLoading(false);
    }
  }, [appliedPeriod]);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  const totals = overview?.totals;
  const listTotal = useMemo(
    () => (overview?.expenditures ?? []).reduce((sum, item) => sum + Number(item.amount ?? 0), 0),
    [overview],
  );

  return (
    <div className="form-card form-card--wide profile-statistics profile-expenses">
      <div className="profile-statistics__header">
        <div>
          <h2>Расходы</h2>
          <p className="muted">Все филиалы и общие расходы кабинета</p>
        </div>
        <div className="profile-statistics__header-actions">
          <span className="muted">{formatPeriodLabel(appliedPeriod)}</span>
          <UiButton type="button" variant="ghost" size="sm" disabled={loading} onClick={() => void loadOverview()}>
            {loading ? 'Обновляем…' : 'Обновить'}
          </UiButton>
        </div>
      </div>

      <div className="profile-statistics__filters">
        <UiDateRangePicker
          from={period.from}
          to={period.to}
          onChange={({ from, to }) => setPeriod({ from, to })}
        />
        <UiButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            const next = currentMonthPeriod();
            setPeriod(next);
            setAppliedPeriod(next);
          }}
        >
          Текущий месяц
        </UiButton>
        <UiButton type="button" onClick={() => setAppliedPeriod(period)} disabled={loading}>
          Применить
        </UiButton>
        <UiLinkButton to="/profile/expenses" variant="ghost" size="sm">
          Общие расходы
        </UiLinkButton>
      </div>

      {error ? <div className="banner banner--danger">{error}</div> : null}
      {loading && !overview ? <div className="placeholder">Загрузка…</div> : null}

      {overview ? (
        <>
          <div className="profile-expenses__metrics">
            <div className="profile-expenses__metric">
              <span>Фактический расход</span>
              <strong>{formatMoney(totals?.actual)}</strong>
              <small>Ручные записи и зарплаты за уже прошедшие занятия</small>
            </div>
            <div className="profile-expenses__metric">
              <span>Планируемый расход</span>
              <strong>{formatMoney(totals?.planned)}</strong>
              <small>Зарплаты педагогов за занятия, которые ещё не прошли</small>
            </div>
          </div>

          <dl className="profile-turnover__rows">
            <div>
              <dt>Ручные расходы</dt>
              <dd>{formatMoney(totals?.manual)}</dd>
            </div>
            <div>
              <dt>Зарплаты (факт / с планом)</dt>
              <dd>{formatMoney(totals?.actualSalary)}/{formatMoney(totals?.plannedSalary)}</dd>
            </div>
            <div>
              <dt>Итого (факт / с планом)</dt>
              <dd>
                {formatMoney(totals?.actual)}/{formatMoney((Number(totals?.actual) || 0) + (Number(totals?.planned) || 0))}
              </dd>
            </div>
          </dl>

          <div className="page-tabs">
            <button
              type="button"
              className={activeTab === 'expenses' ? 'active' : ''}
              onClick={() => setActiveTab('expenses')}
            >
              Список расходов
            </button>
            <button
              type="button"
              className={activeTab === 'salaries' ? 'active' : ''}
              onClick={() => setActiveTab('salaries')}
            >
              Зарплаты педагогов
            </button>
            <button
              type="button"
              className={activeTab === 'branches' ? 'active' : ''}
              onClick={() => setActiveTab('branches')}
            >
              По филиалам
            </button>
          </div>

          {activeTab === 'expenses' ? (
            <div className="profile-statistics__table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Филиал</th>
                    <th>Название</th>
                    <th>Группа</th>
                    <th style={{ textAlign: 'right' }}>Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview.expenditures ?? []).map((item) => (
                    <tr key={item.id}>
                      <td>{item.spentAt ?? '—'}</td>
                      <td>{item.branchName}</td>
                      <td>{item.name ?? '—'}</td>
                      <td>{item.groupName ?? item.groupId ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(item.amount)}</td>
                    </tr>
                  ))}
                  {!overview.expenditures?.length && (
                    <tr>
                      <td colSpan={5} className="muted" style={{ textAlign: 'center' }}>
                        За период расходов нет
                      </td>
                    </tr>
                  )}
                </tbody>
                {overview.expenditures?.length ? (
                  <tfoot>
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'right', fontWeight: 500 }}>
                        Итого:
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(listTotal)}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          ) : null}

          {activeTab === 'salaries' ? (
            <div className="profile-statistics__table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Педагог</th>
                    <th style={{ textAlign: 'right' }}>Проведено</th>
                    <th style={{ textAlign: 'right' }}>Ещё не прошло</th>
                    <th style={{ textAlign: 'right' }}>Фактическая зарплата</th>
                    <th style={{ textAlign: 'right' }}>Планируемая зарплата</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview.salaries ?? []).map((row) => (
                    <tr key={row.managerId}>
                      <td>{row.managerName ?? row.managerId}</td>
                      <td style={{ textAlign: 'right' }}>{row.actualLessonsCount}</td>
                      <td style={{ textAlign: 'right' }}>{row.futureLessonsCount}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(row.actualTotal)}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(row.plannedTotal)}</td>
                    </tr>
                  ))}
                  {!overview.salaries?.length && (
                    <tr>
                      <td colSpan={5} className="muted" style={{ textAlign: 'center' }}>
                        За период зарплат нет
                      </td>
                    </tr>
                  )}
                </tbody>
                {overview.salaries?.length ? (
                  <tfoot>
                    <tr>
                      <td colSpan={3} style={{ textAlign: 'right', fontWeight: 500 }}>
                        Итого:
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(totals?.actualSalary)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(totals?.plannedSalary)}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          ) : null}

          {activeTab === 'branches' ? (
            <div className="profile-statistics__table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Филиал</th>
                    <th style={{ textAlign: 'right' }}>Ручные</th>
                    <th style={{ textAlign: 'right' }}>Факт ЗП</th>
                    <th style={{ textAlign: 'right' }}>План ЗП</th>
                    <th style={{ textAlign: 'right' }}>Фактический итог</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview.branches ?? []).map((row) => (
                    <tr key={row.id ?? 'common'}>
                      <td>{row.name}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(row.manual)}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(row.actualSalary)}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(row.plannedSalary)}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(row.actual)}</td>
                    </tr>
                  ))}
                  {!overview.branches?.length && (
                    <tr>
                      <td colSpan={5} className="muted" style={{ textAlign: 'center' }}>
                        За период данных нет
                      </td>
                    </tr>
                  )}
                </tbody>
                {overview.branches?.length ? (
                  <tfoot>
                    <tr>
                      <td style={{ textAlign: 'right', fontWeight: 500 }}>Итого:</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(totals?.manual)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(totals?.actualSalary)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(totals?.plannedSalary)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(totals?.actual)}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
